import { useState, useEffect, useCallback } from 'react';
import {
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineBadgeCheck,
  HiOutlineChartPie,
  HiOutlineCalendar,
  HiOutlineFilter,
  HiOutlineX,
  HiOutlineBookOpen,
  HiOutlineDocumentText,
  HiOutlineQuestionMarkCircle,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineChatAlt,
  HiOutlineHeart,
  HiOutlineAnnotation,
  HiOutlineUserCircle,
  HiOutlineMicrophone,
  HiOutlineBell,
  HiOutlineDownload,
  HiOutlineSparkles,
  HiOutlineExclamationCircle
} from 'react-icons/hi';
import type { DashboardTotalsData, DashboardTotalsResponse, DateFilters } from '../services/trainingDashboardApi';
import { getDashboardTotals, exportCapacitaciones, EXPORT_NO_DATA_ERROR } from '../services/trainingDashboardApi';
import trainingService from '../services/trainingsApi';
import type { TrainingItem } from '../services/trainingsApi';

interface SliceData {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

interface PieChartProps {
  data: SliceData[];
  size?: number;
  title: string;
}

function PieChart({ data, size = 180, title }: PieChartProps): JSX.Element {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulative = 0;

  const buildSlice = (percentage: number, color: string, index: number) => {
    const startAngle = cumulative * 3.6;
    cumulative += percentage;
    const endAngle = cumulative * 3.6;
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    const radius = size / 2 - 10;
    const cx = size / 2;
    const cy = size / 2;
    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);
    const largeArc = percentage > 50 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    return (
      <path
        key={index}
        d={d}
        fill={color}
        stroke="var(--bg-card)"
        strokeWidth="2"
        style={{ transition: 'transform 0.3s ease', transformOrigin: 'center', cursor: 'pointer' }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      />
    );
  };

  cumulative = 0;

  return (
    <div>
      <h6 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <HiOutlineChartPie style={{ color: 'var(--primary)' }} />
        {title}
      </h6>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {data.map((item, index) => buildSlice(item.percentage, item.color, index))}
          <circle cx={size / 2} cy={size / 2} r={size / 4} fill="var(--bg-card)" />
          <text x={size / 2} y={size / 2 - 8} textAnchor="middle" style={{ fontSize: '1.25rem', fontWeight: 700, fill: 'var(--text-primary)' }}>
            {total.toLocaleString()}
          </text>
          <text x={size / 2} y={size / 2 + 12} textAnchor="middle" style={{ fontSize: '0.75rem', fill: 'var(--text-muted)' }}>
            Total
          </text>
        </svg>
        <div style={{ flex: 1, minWidth: '150px' }}>
          {data.map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: item.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500 }}>{item.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {item.value.toLocaleString()} ({item.percentage}%)
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface MetricTileProps {
  label: string;
  value: number;
  color: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
}

function MetricTile({ label, value, color, icon: Icon }: MetricTileProps): JSX.Element {
  return (
    <div style={{ padding: '1rem', background: 'var(--bg-body)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-md)', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon style={{ color, fontSize: '1.25rem' }} />
      </div>
      <div>
        <div style={{ fontSize: '1.375rem', fontWeight: 700, color, fontFamily: 'var(--font-display)', lineHeight: 1 }}>
          {value.toLocaleString()}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '3px' }}>{label}</div>
      </div>
    </div>
  );
}

interface AlertBannerProps {
  message: string;
  onClose: () => void;
}

function AlertBanner({ message, onClose }: AlertBannerProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '1rem 1.25rem',
        marginBottom: '1rem',
        background: 'var(--accent-orange)12',
        border: '1px solid var(--accent-orange)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--accent-orange)',
        fontSize: '0.875rem',
        fontWeight: 500,
        animation: 'fadeIn 0.3s ease'
      }}
    >
      <HiOutlineExclamationCircle style={{ fontSize: '1.25rem', flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--accent-orange)',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--radius-sm)',
          flexShrink: 0
        }}
      >
        <HiOutlineX style={{ fontSize: '1rem' }} />
      </button>
    </div>
  );
}

function buildContentSlices(d: DashboardTotalsData): SliceData[] {
  const total = d.totalMateriales + d.totalArchivos + d.totalCuestionarios + d.totalPodcast + d.totalFaq;
  if (total === 0) return [];
  return [
    { label: 'Materiales', value: d.totalMateriales, percentage: Math.round((d.totalMateriales / total) * 100), color: 'var(--primary)' },
    { label: 'Archivos', value: d.totalArchivos, percentage: Math.round((d.totalArchivos / total) * 100), color: 'var(--secondary)' },
    { label: 'Cuestionarios', value: d.totalCuestionarios, percentage: Math.round((d.totalCuestionarios / total) * 100), color: 'var(--accent-cyan)' },
    { label: 'Podcast', value: d.totalPodcast, percentage: Math.round((d.totalPodcast / total) * 100), color: 'var(--accent-green)' },
    { label: 'FAQ', value: d.totalFaq, percentage: Math.round((d.totalFaq / total) * 100), color: 'var(--accent-orange)' },
  ].filter(s => s.value > 0);
}

function buildCapacitacionSlices(d: DashboardTotalsData): SliceData[] {
  const total = d.totalAsignacionesCapacitacion + d.totalIntentosCapacitacion + d.totalCertificados + d.totalReprobados;
  if (total === 0) return [];
  return [
    { label: 'Asignaciones', value: d.totalAsignacionesCapacitacion, percentage: Math.round((d.totalAsignacionesCapacitacion / total) * 100), color: 'var(--primary)' },
    { label: 'Intentos', value: d.totalIntentosCapacitacion, percentage: Math.round((d.totalIntentosCapacitacion / total) * 100), color: 'var(--secondary)' },
    { label: 'Certificados', value: d.totalCertificados, percentage: Math.round((d.totalCertificados / total) * 100), color: 'var(--accent-green)' },
    { label: 'Reprobados', value: d.totalReprobados, percentage: Math.round((d.totalReprobados / total) * 100), color: 'var(--accent-orange)' },
  ].filter(s => s.value > 0);
}

function buildActivitySlices(d: DashboardTotalsData): SliceData[] {
  if (d.totalUsuarios === 0) return [];
  return [
    { label: 'Activos', value: d.usuariosActivos, percentage: Math.round((d.usuariosActivos / d.totalUsuarios) * 100), color: 'var(--accent-cyan)' },
    { label: 'Inactivos', value: d.usuariosInactivos, percentage: Math.round((d.usuariosInactivos / d.totalUsuarios) * 100), color: 'var(--accent-orange)' },
  ].filter(s => s.value > 0);
}

function getDefaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0] ?? '';
}

function getDefaultEndDate(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function TrainingDashboardPage(): JSX.Element {
  const [data, setData] = useState<DashboardTotalsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [exportAlert, setExportAlert] = useState<string | null>(null);

  const [capacitaciones, setCapacitaciones] = useState<TrainingItem[]>([]);
  const [loadingCapacitaciones, setLoadingCapacitaciones] = useState(true);

  const [filters, setFilters] = useState<DateFilters>({
    fechaInicio: getDefaultStartDate(),
    fechaFin: getDefaultEndDate(),
    idCapacitacion: null
  });
  const [appliedFilters, setAppliedFilters] = useState<DateFilters>(filters);

  const fetchCapacitaciones = useCallback(async () => {
    setLoadingCapacitaciones(true);
    try {
      const response = await trainingService.getItems(1, 200);
      setCapacitaciones(response.data.content);
    } catch (err) {
      console.error('Error loading capacitaciones:', err);
    } finally {
      setLoadingCapacitaciones(false);
    }
  }, []);

  useEffect(() => {
    void fetchCapacitaciones();
  }, [fetchCapacitaciones]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: DashboardTotalsResponse = await getDashboardTotals(appliedFilters);
      setData(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleFilter = async () => {
    setIsFiltering(true);
    setExportAlert(null);
    setAppliedFilters({ ...filters });
    setIsFiltering(false);
  };

  const handleClear = () => {
    const defaultFilters: DateFilters = {
      fechaInicio: getDefaultStartDate(),
      fechaFin: getDefaultEndDate(),
      idCapacitacion: null
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setExportAlert(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportAlert(null);
    try {
      await exportCapacitaciones(appliedFilters);
    } catch (err) {
      if (err instanceof Error && err.message === EXPORT_NO_DATA_ERROR) {
        setExportAlert('No hay datos disponibles para descargar con los filtros seleccionados.');
      } else {
        setExportAlert('Ocurrió un error al exportar el reporte. Intente de nuevo.');
        console.error('Error exporting report:', err);
      }
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="training-dashboard-page fade-in">
        <div className="card">
          <div className="card-body" style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-danger)', marginBottom: '1rem' }}>
              Error al cargar los datos: {error}
            </p>
            <button className="btn btn-primary" onClick={() => void fetchData()}>
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="training-dashboard-page fade-in">
      <div className="page-header mb-4">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="page-title" style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>
              Dashboard de Capacitaciones
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem' }}>
              Analíticas y métricas de los programas de capacitación
            </p>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => void handleExport()}
            disabled={exporting}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <HiOutlineDownload />
            {exporting ? 'Exportando...' : 'Exportar Reporte'}
          </button>
        </div>
      </div>

      {exportAlert && (
        <AlertBanner
          message={exportAlert}
          onClose={() => setExportAlert(null)}
        />
      )}

      <div className="card mb-4">
        <div className="card-body" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)', fontWeight: 600 }}>
              <HiOutlineCalendar style={{ fontSize: '1.25rem' }} />
              <span>Filtros:</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', flex: 1 }}>
              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.fechaInicio}
                  onChange={(e) => setFilters(prev => ({ ...prev, fechaInicio: e.target.value }))}
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                />
              </div>

              <div style={{ minWidth: '160px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>
                  Fecha Fin
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.fechaFin}
                  onChange={(e) => setFilters(prev => ({ ...prev, fechaFin: e.target.value }))}
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                />
              </div>

              <div style={{ minWidth: '220px', flex: 1, maxWidth: '350px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>
                  Capacitación
                </label>
                <select
                  className="form-control"
                  value={filters.idCapacitacion ?? ''}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    idCapacitacion: e.target.value ? Number(e.target.value) : null
                  }))}
                  disabled={loadingCapacitaciones}
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  <option value="">
                    {loadingCapacitaciones ? 'Cargando...' : 'Todas las capacitaciones'}
                  </option>
                  {capacitaciones.map((cap) => (
                    <option key={cap.id} value={cap.id}>
                      {cap.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-primary"
                onClick={() => void handleFilter()}
                disabled={isFiltering}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {isFiltering ? <span className="spinner" style={{ width: '16px', height: '16px' }} /> : <HiOutlineFilter />}
                Filtrar
              </button>
              <button
                className="btn btn-outline"
                onClick={handleClear}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <HiOutlineX />
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="card-body" style={{ padding: '2rem', textAlign: 'center' }}>
            <span className="spinner" style={{ width: '32px', height: '32px' }} />
          </div>
        </div>
      ) : data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="stats-card-modern purple">
              <div className="stats-icon"><HiOutlineUsers /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.totalUsuarios.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Usuarios Totales</div>
              <div className="trend-up" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineTrendingUp /> Activos: {data.usuariosActivos}
              </div>
            </div>

            <div className="stats-card-modern cyan">
              <div className="stats-icon"><HiOutlineBookOpen /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.totalCapacitaciones.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Capacitaciones</div>
              <div className="trend-up" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineUserCircle /> Con actividad: {data.usuariosConActividadCapacitacion}
              </div>
            </div>

            <div className="stats-card-modern green">
              <div className="stats-icon"><HiOutlineDocumentText /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.totalMateriales.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Materiales</div>
              <div className="trend-up" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineTrendingUp /> Archivos: {data.totalArchivos}
              </div>
            </div>

            <div className="stats-card-modern orange">
              <div className="stats-icon"><HiOutlineUserGroup /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.usuariosActivos.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Usuarios Activos</div>
              <div className="trend-down" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineTrendingDown /> Inactivos: {data.usuariosInactivos}
              </div>
            </div>

            <div className="stats-card-modern pink">
              <div className="stats-icon"><HiOutlineQuestionMarkCircle /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.totalCuestionarios.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Cuestionarios</div>
              <div className="trend-up" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineTrendingUp /> Respuestas: {data.totalRespuestasCuestionarios}
              </div>
            </div>

            <div className="stats-card-modern teal">
              <div className="stats-icon"><HiOutlineBadgeCheck /></div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px', fontFamily: 'var(--font-display)' }}>{data.totalCertificados.toLocaleString()}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Certificados</div>
              <div className="trend-down" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600 }}>
                <HiOutlineTrendingDown /> Reprobados: {data.totalReprobados}
              </div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            {buildContentSlices(data).length > 0 && (
              <div className="col-lg-4">
                <div className="card card-glow h-100">
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <PieChart data={buildContentSlices(data)} title="Distribución de Contenido" />
                  </div>
                </div>
              </div>
            )}

            {buildCapacitacionSlices(data).length > 0 && (
              <div className="col-lg-4">
                <div className="card card-glow h-100">
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <PieChart data={buildCapacitacionSlices(data)} title="Distribución Capacitaciones" />
                  </div>
                </div>
              </div>
            )}

            {buildActivitySlices(data).length > 0 && (
              <div className="col-lg-4">
                <div className="card card-glow h-100">
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <PieChart data={buildActivitySlices(data)} title="Actividad de Usuarios" size={160} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card card-glow h-100">
                <div className="card-body" style={{ padding: '1.5rem' }}>
                  <h6 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HiOutlineBookOpen style={{ color: 'var(--primary)' }} />
                    Capacitación
                  </h6>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                    <MetricTile label="Asignaciones" value={data.totalAsignacionesCapacitacion} color="var(--primary)" icon={HiOutlineBookOpen} />
                    <MetricTile label="Intentos" value={data.totalIntentosCapacitacion} color="var(--secondary)" icon={HiOutlineTrendingUp} />
                    <MetricTile label="Certificados" value={data.totalCertificados} color="var(--accent-green)" icon={HiOutlineBadgeCheck} />
                    <MetricTile label="Reprobados" value={data.totalReprobados} color="var(--accent-orange)" icon={HiOutlineTrendingDown} />
                    <MetricTile label="Usuarios con Actividad" value={data.usuariosConActividadCapacitacion} color="var(--accent-cyan)" icon={HiOutlineUserCircle} />
                    <MetricTile label="Resp. Cuestionarios" value={data.totalRespuestasCuestionarios} color="var(--primary)" icon={HiOutlineQuestionMarkCircle} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card card-glow h-100">
                <div className="card-body" style={{ padding: '1.5rem' }}>
                  <h6 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HiOutlineChatAlt style={{ color: 'var(--secondary)' }} />
                    Comunidad y Contenido
                  </h6>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                    <MetricTile label="Comunidades" value={data.totalComunidades} color="var(--primary)" icon={HiOutlineUsers} />
                    <MetricTile label="Usuarios Comunidad" value={data.totalUsuariosComunidad} color="var(--secondary)" icon={HiOutlineUserGroup} />
                    <MetricTile label="Publicaciones" value={data.totalPublicaciones} color="var(--accent-cyan)" icon={HiOutlineDocumentText} />
                    <MetricTile label="Comentarios Comunidad" value={data.totalComentariosComunidad} color="var(--accent-green)" icon={HiOutlineChatAlt} />
                    <MetricTile label="Comentarios Muro" value={data.totalComentariosMuro} color="var(--accent-orange)" icon={HiOutlineAnnotation} />
                    <MetricTile label="Likes Muro" value={data.totalLikesMuro} color="var(--primary)" icon={HiOutlineHeart} />
                    <MetricTile label="Usuarios Interactúan" value={data.usuariosQueInteractuan} color="var(--secondary)" icon={HiOutlineSparkles} />
                    <MetricTile label="Materiales" value={data.totalMateriales} color="var(--accent-cyan)" icon={HiOutlineDocumentText} />
                    <MetricTile label="Archivos" value={data.totalArchivos} color="var(--accent-green)" icon={HiOutlineDocumentText} />
                    <MetricTile label="Podcast" value={data.totalPodcast} color="var(--accent-orange)" icon={HiOutlineMicrophone} />
                    <MetricTile label="FAQ" value={data.totalFaq} color="var(--primary)" icon={HiOutlineQuestionMarkCircle} />
                    <MetricTile label="Notificaciones" value={data.totalNotificaciones} color="var(--secondary)" icon={HiOutlineBell} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TrainingDashboardPage;